import React, {useRef, useState} from 'react';
import {FACTION_CONFIGS, FACTION_CONFIGS_IMPLEMENTED} from "../../../configs/factions";
import {getFactionImagePath} from "../../../utils/factions/getFactionImagePath";
import {getFactionUnitSkinIconPath} from "../../../utils/factions/getFactionUnitSkinIconPath";
import FactionCardOverlay from "../../FactionCardOverlay/FactionCardOverlay";
import PlayerFactionIcon from "../../PlayerFactionIcon/PlayerFactionIcon";
import PageButton from "../../elements/PageButton/PageButton";
import UNIT_TYPES from "../../../configs/units";
import {FACTION_DETAILED_RULES} from "./factionDetailedRules";
import {MENU_SOUND_MANAGER} from "../../../scenes/MenuSoundManager";

// Подложка значка фракции в заголовке — та же, что у плашки фракции в
// инвентаре (.inventory-faction-item)
const FACTION_ICON_BACKGROUND = 'rgba(255, 255, 255, 0.05)';

// Значок элитного отряда фракции для заголовка ее правил: d20, а если такого
// типа у фракции нет — последний тип из ее перечня (порядок в конфиге —
// порядок тулбара найма)
const getFactionEliteUnitTypeId = (faction) => {
  const factionUnitTypeIds = faction.unitTypes || Object.values(UNIT_TYPES).map((unitType) => unitType.id);
  return factionUnitTypeIds.includes(UNIT_TYPES.D20.id)
    ? UNIT_TYPES.D20.id
    : factionUnitTypeIds[factionUnitTypeIds.length - 1];
};

const FactionsRules = () => {
  const [selectedFactionId, setSelectedFactionId] = useState(null);
  // Якоря блоков подробных правил: кнопка «Подробно» на карточке фракции
  // прокручивает страницу правил к нужному блоку
  const factionRulesRefs = useRef({});

  // Звук выбранной фракции — как при выборе фракции перед матчем. Он есть
  // только у меню-менеджера (MenuSoundManager): правила, открытые ИЗ МАТЧА,
  // получают игровой SoundManager, где фракционных звуков нет вовсе — там
  // откатываемся на общий звук открытия карточки. Звук дает обработчик, а не
  // PageButton (у него noSound) — иначе к нему добавился бы обычный клик
  const handleOpenFaction = (factionId) => {
    MENU_SOUND_MANAGER.playFactionSelect(factionId, true);
    setSelectedFactionId(factionId);
  };

  const handleScrollToFactionRules = (factionId) => {
    MENU_SOUND_MANAGER.playMenuButtonClick();
    factionRulesRefs.current[factionId]?.scrollIntoView({behavior: 'smooth', block: 'start'});
  };

  const factionsInDevelopment = FACTION_CONFIGS.filter((faction) => !faction.implemented);

  return (
    <div className="rules-page_content">
      <h2 className="rules-page_title text-primary">
        Фракции
      </h2>

      <div className="rules-page_section">
        <p>
          Перед стартом игры каждый игрок выбирает фракцию, за которую будет играть всю игру. Каждая фракция имеет
          свои уникальные бонусы, а иногда и особые механики.
        </p>

        <p>
          В рейтинговых матчах игрок для выбора получает только 4 случайные фракции из перечня ему доступных.
        </p>

        <p>
          Ниже приведен перечень всех фракций:
        </p>
      </div>

      <div className="rules-page_section">
        <div className="rules-page_factions-grid">
          {FACTION_CONFIGS_IMPLEMENTED.map((faction) => (
            <div
              key={faction.id}
              className="rules-page_faction-card"
              onClick={() => handleOpenFaction(faction.id)}
            >
              <img
                className="rules-page_faction-card_image"
                src={getFactionImagePath(faction.id)}
                alt={faction.name}
              />
              <div className="rules-page_faction-card_name" style={{color: faction.textColor}}>
                {faction.name}
              </div>
              <div className="rules-page_faction-card_description">
                {faction.description}
              </div>
              <div className="rules-page_faction-card_buttons">
                <PageButton
                  className="rules-page_card-details-button"
                  pageId="FactionsRules"
                  buttonId={`faction_card_${faction.id}`}
                  noSound={true}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleOpenFaction(faction.id);
                  }}
                >
                  Карточка
                </PageButton>
                <PageButton
                  className="rules-page_card-details-button"
                  pageId="FactionsRules"
                  buttonId={`faction_details_${faction.id}`}
                  noSound={true}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleScrollToFactionRules(faction.id);
                  }}
                >
                  Подробно
                </PageButton>
              </div>
            </div>
          ))}
        </div>
      </div>

      {factionsInDevelopment.length > 0 && (
        <div className="rules-page_section">
          <h3 className="rules-page_factions-subtitle">В разработке</h3>
          <div className="rules-page_factions-grid">
            {factionsInDevelopment.map((faction) => (
              <div
                key={faction.id}
                className="rules-page_faction-card rules-page_faction-card_in-development"
              >
                <img
                  className="rules-page_faction-card_image"
                  src={getFactionImagePath(faction.id)}
                  alt={faction.name}
                />
                <div className="rules-page_faction-card_name" style={{color: faction.textColor}}>
                  {faction.name}
                </div>
                <div className="rules-page_faction-card_description">
                  {faction.description}
                </div>
                <PageButton
                  className="rules-page_card-details-button"
                  pageId="FactionsRules"
                  buttonId={`faction_details_${faction.id}`}
                  disabled
                  title="Фракция еще в разработке"
                >
                  В разработке
                </PageButton>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="rules-page_section">
        <h2>Подробные правила фракций</h2>

        {FACTION_CONFIGS_IMPLEMENTED.map((faction) => {
          const FactionRulesComponent = FACTION_DETAILED_RULES[faction.id];
          if (!FactionRulesComponent) {
            return null;
          }

          return (
            <div
              key={faction.id}
              className="rules-page_faction-rules-block"
              /* Цвет фракции — переменной на блок: им красятся подзаголовки
                 абзацев внутри правил (см. Subheading в factionDetailedRules) */
              style={{'--faction-text-color': faction.textColor}}
              ref={(element) => {
                factionRulesRefs.current[faction.id] = element;
              }}
            >
              <h3 className="rules-page_faction-rules-heading">
                <span className="rules-page_faction-rules-heading_icons">
                  {/* Подложка — нейтральная, как плашка фракции в инвентаре:
                      иконка читается в собственном цвете фракции, а не на
                      фоне цвета игрока */}
                  <PlayerFactionIcon
                    player={{}}
                    factionId={faction.id}
                    backgroundColor={FACTION_ICON_BACKGROUND}
                  />
                  {/* Значок отряда — белый силуэт: красим его маской в цвет
                      фракции, как это делает инвентарь для образов отрядов
                      (getFactionSkinSelectionColor без выбранного скина
                      возвращает как раз faction.textColor) */}
                  <span
                    className="rules-page_faction-rules-heading_unit-icon"
                    style={{
                      backgroundColor: faction.textColor,
                      WebkitMaskImage: `url(${getFactionUnitSkinIconPath(getFactionEliteUnitTypeId(faction), faction.id)})`,
                      maskImage: `url(${getFactionUnitSkinIconPath(getFactionEliteUnitTypeId(faction), faction.id)})`,
                    }}
                    role="img"
                    aria-label={faction.name}
                    title={faction.name}
                  />
                </span>
                <span style={{color: faction.textColor}}>{faction.name}</span>
              </h3>

              {/* Ссылка-кнопка: открывает ту же карточку фракции, что и
                    кнопка «Карточка» на плашке выше */}
              <button
                type="button"
                className="rules-page_faction-rules-heading_card-link text-controls"
                onClick={() => handleOpenFaction(faction.id)}
              >
                Карточка
              </button>

              <FactionRulesComponent/>
            </div>
          );
        })}
      </div>

      {selectedFactionId && (
        <FactionCardOverlay
          factionId={selectedFactionId}
          onClose={() => setSelectedFactionId(null)}
        />
      )}
    </div>
  );
};

export default FactionsRules;
